import 'chart.js/auto';
import { Chart as ChatJS } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useMemo, useState, useEffect, useCallback } from 'react';
import 'react-tooltip/dist/react-tooltip.css';
import { RootState, useAppDispatch, useAppSelector } from 'store';
import {
  Dataset,
  getDataCharts,
  HasData
} from 'utils/chart-options/chart-collection';
import { ChartDataMap, CompanyMetrics } from 'utils/helpers/chartsDataHelpers';
import ChartContainer, {
  chartsToHideAvgDealSizeSwitch
} from './ChartContainer';
import { Tabs, TabsContent } from './TabComponent';
import HorizontalAccordion from 'components/HorizontalAccordion';
import { getCharts } from './charts';
import ChartsNavTabs from './ChartsNavTabs';
import { Switch } from './Switch';
import {
  setChartsData,
  toggleSidebarOpen,
  setSelectedChartIndex
} from 'store/chartsInfo/chartsInfo.slice';
import SidebarTabs from './SidebarTabs';
import ChartsForms from './ChartsForms';
import { handleXAxisSwitch } from 'store/chartSwitches/chartSwitches.slice';

ChatJS.register(ChartDataLabels);
interface ISectionCharts {
  chartDataMap: ChartDataMap[];
}
const SectionCharts = ({ chartDataMap }: ISectionCharts) => {
  const {
    requiredInfo,
    growthAndRetention,
    operationalEfficiency,
    headCount,
    companyDetails
  } = useAppSelector((state: RootState) => state.companyInfo);

  const dispatch = useAppDispatch();

  const quartileMedianSwitch = useAppSelector(
    (state: RootState) => state.chartSwitches.quartileMedianSwitch
  );

  const logoFromStorage = localStorage.getItem('companyLogoURL');

  const [companyLogoURL, setCompanyLogoURL] = useState(
    companyDetails?.companyLogoURL || logoFromStorage || ''
  );

  const [chartKey, setChartKey] = useState(0);
  const [showNavBtns, setShowNavBtns] = useState(false);
  const [selectedSidebarTab, setSelectedSidebarTab] = useState(0);

  const handleShowNavBtns = useCallback(() => {
    setShowNavBtns(prev => !prev);
    dispatch(toggleSidebarOpen());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectedSidebarTab = (tab: number) => {
    setSelectedSidebarTab(tab);
  };

  useEffect(() => {
    setCompanyLogoURL(companyDetails?.companyLogoURL || logoFromStorage || '');
    setChartKey(prev => prev + 1);
  }, [companyDetails?.companyLogoURL, logoFromStorage]);

  const companyMetrics: CompanyMetrics = {
    ...requiredInfo,
    ...growthAndRetention,
    ...operationalEfficiency,
    ...headCount,
    ...companyDetails
  };
  const [currentChartIndex, setCurrentChartIndex] = useState(0);
  const [presentationMode, setPresentationMode] = useState(false);

  const chartFilters = useAppSelector((state: RootState) => state.chartFilters);
  const chartSwitches = useAppSelector(
    (state: RootState) => state.chartSwitches
  );

  const { generatingPDF, downloadIndex } = useAppSelector(
    state => state.dashboardPDF
  );

  const { barChartData, bubbleChartData } = getDataCharts(
    quartileMedianSwitch,
    chartDataMap,
    companyMetrics,
    chartFilters,
    chartSwitches
  );

  const hasData: HasData = (
    dataset: Dataset,
    labels: string[],
    isRuleof40?: boolean
  ) => {
    if (isRuleof40) {
      // super hacky
      // temp until refactor
      // trying to accommodate:
      //  - never showing <$25M ARR group (ie: r == 10 group) due to currently large outlier data
      //  - client recently removed this group from data entirely
      //    - but we are also _typically_ hiding entire chart if _any_ ARR range is missing
      //    - so since that would cause Rule of 40 to never show, we are specially handling this case
      const datasetRemovedR10 = {
        label: dataset.label,
        data: dataset.data.filter((item: any) => item && item.r !== 10),
        backgroundColor: dataset.backgroundColor
      };

      const isInfrastructure =
        chartFilters.softwareSector.length === 1 &&
        chartFilters.softwareSector?.[0].value === 'Infrastructure';

      const isProductLedGrowth =
        chartFilters.growthMotion.length === 1 &&
        chartFilters.growthMotion?.[0]?.value === 'Product-led Growth';

      const isSmbToMidMarket =
        chartFilters.primaryCustomer.length === 1 &&
        chartFilters.primaryCustomer?.[0]?.value === 'Mid-Market to Enterprise';

      const shouldHide =
        isInfrastructure && isProductLedGrowth && isSmbToMidMarket;

      return (
        !shouldHide &&
        datasetRemovedR10 &&
        dataset.data.length === 4 &&
        !dataset?.data.map((d: any) => d.x).includes(null) &&
        !dataset?.data.map((d: any) => d.y).includes(null) &&
        !dataset?.data.map((d: any) => d.ruleOf40).includes(null)
      );
    }

    return (
      dataset &&
      dataset.data.length > 0 &&
      dataset.data.length === labels.length &&
      !dataset?.data.includes(null) &&
      !dataset?.data?.includes(undefined)
    );
  };

  const chartData = getCharts({
    companyLogoURL,
    chartKey,
    generatingPDF,
    barChartData,
    bubbleChartData,
    hasData,
    xAxisSwitch: chartSwitches.xAxisSwitch
  });

  const flatChartsData = useMemo(
    () => chartData.flatMap(item => item.charts),
    [chartData]
  );

  const prevChart =
    (showNavBtns &&
      currentChartIndex > 0 &&
      flatChartsData[currentChartIndex - 1]?.title) ||
    '';

  const nextChart =
    (showNavBtns &&
      currentChartIndex < flatChartsData.length - 1 &&
      flatChartsData[currentChartIndex + 1]?.title) ||
    '';

  const handleChartIndex = (newIndex: number) => {
    const newTitle = flatChartsData[newIndex]?.title;
    if (chartsToHideAvgDealSizeSwitch.includes(newTitle)) {
      dispatch(handleXAxisSwitch(true));
    }

    setCurrentChartIndex(newIndex);
  };

  const handlePresentationMode = useCallback(() => {
    setPresentationMode(prev => !prev);
  }, []);

  useEffect(() => {
    if (!presentationMode) return;

    const interval = setInterval(() => {
      setCurrentChartIndex(prev =>
        prev === flatChartsData.length - 1 ? 0 : prev + 1
      );
    }, 7 * 1000);

    return () => clearInterval(interval);
  }, [presentationMode, flatChartsData]);

  useEffect(() => {
    dispatch(
      setChartsData(
        flatChartsData.map(({ id, title, ...rest }) => ({ id, title }))
      )
    );
    dispatch(setSelectedChartIndex(currentChartIndex));
  }, [dispatch, flatChartsData, currentChartIndex]);

  return (
    <section className='w-full px-20 pb-20'>
      <Tabs
        defaultValue='0'
        value={String(currentChartIndex)}
        onValueChange={value => setCurrentChartIndex(Number(value))}
      >
        <div className='w-full flex flex-row items-start gap-10'>
          <HorizontalAccordion
            tabs={
              <SidebarTabs
                handleToggle={handleSelectedSidebarTab}
                selectedSidebarTab={selectedSidebarTab}
              />
            }
            className={`walkthrough-tabs-list ${
              showNavBtns ? 'ml-[-81px] rounded-l-none' : ''
            }`}
            onChange={handleShowNavBtns}
            footer={
              selectedSidebarTab === 0 ? (
                <div
                  onClick={handlePresentationMode}
                  className='flex items-center gap-2 width-min'
                >
                  <Switch
                    checkedColor='#4DB96B'
                    uncheckedColor='#9B9B9B'
                    checked={presentationMode}
                  />
                  <p className='select-none whitespace-nowrap text-[##404040]'>
                    Presentation mode
                  </p>
                </div>
              ) : undefined
            }
            content={
              selectedSidebarTab === 0 ? (
                <ChartsNavTabs
                  chartData={chartData}
                  downloadIndex={downloadIndex}
                  handleChartIndex={handleChartIndex}
                  presentationMode={presentationMode}
                  currentChartIndex={currentChartIndex}
                />
              ) : (
                <ChartsForms />
              )
            }
          />
          {flatChartsData.map(item => (
            <TabsContent
              key={String(item.title + item.id)}
              value={String(downloadIndex === 1 ? currentChartIndex : item.id)}
              className='flex-1'
            >
              <ChartContainer
                title={item.title}
                tooltip={item.tooltipContent}
                chartData={flatChartsData}
                prevChart={prevChart}
                nextChart={nextChart}
                handleChartIndex={handleChartIndex}
                currentChartIndex={currentChartIndex}
              >
                {item.component}
              </ChartContainer>
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </section>
  );
};

export default SectionCharts;
